import { useMemo, useState } from 'react'
import { useReadContract } from 'wagmi'
import { parseUnits } from 'viem'
import { T } from '@/lib/theme'
import { Button, Input, MaxLink, Panel, SectionLabel } from '@/components/ui'
import { SimulationRows } from '@/components/dashboard/detail/SimulationRows'
import { ADDRESSES, ZERO_ADDR } from '@/lib/contracts'
import { ERC20_ABI } from '@/lib/erc20'
import { useAction } from '@/hooks/useAction'
import multiplyAbi from '@/lib/abis/MultiplyRouter.json'
import poolAbi from '@/lib/abis/HorizonLendingPool.json'
import { POLL_SLOW, fmt, fmtBalance, fmtPct, fmtUsd, weiToInputString, toNumber } from '@/lib/utils'
import { netApyBps, loopLegRates, currentLeverage, liquidationPrice } from '@/lib/loops'
import { effectiveBorrowMaxWei } from '@/lib/reserveCaps'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { useLstApr, intrinsicAprBpsFor } from '@/hooks/useLstApr'

const YEAR_SECONDS = 365 * 24 * 60 * 60

const MAX_UINT = (1n << 256n) - 1n
/// Measured on a fork against a real seeded venue (CHANGELOG.md item 125), for the path this form
/// actually calls. Stated rather than estimated live: a gas estimate against a dormant or
/// unconfigured venue reverts, and a number that disappears exactly when someone is deciding
/// whether to open is worse than a measured constant.
///
/// These replace the 3,046,266 previously quoted here, which was the ITERATIVE `open` measured
/// before one-step existed - so the form was advertising 3.8x the gas of the function it calls,
/// and the position-size guidance derived from it was overstated by the same factor.
const OPEN_GAS = 809_760
const CLOSE_GAS = 806_467
const ROUND_TRIP_GAS = OPEN_GAS + CLOSE_GAS

/// Open/close for one loop, plus the position it produced.
///
/// **The authorisation checklist is the substantive change here.** This used to be one button that
/// silently changed meaning four times - `Approve wstETH`, then `Authorise ETH`, then
/// `Authorise one-step`, then `Open position` - with nothing indicating that three transactions
/// preceded the fourth. Someone reaching "Open position" had already signed three things they were
/// never told about. All three are now visible from the start, with the button naming which step
/// it is on.
export function LoopForm({ loop, user, onTx }) {
  const action = useAction()
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()
  const { aprBps: lstAprBps } = useLstApr()
  const [amount, setAmount] = useState('')
  const [leverage, setLeverage] = useState(2)
  const { collateral, debt } = loop

  const wei = useMemo(() => {
    try { return amount ? parseUnits(amount, collateral.decimals) : 0n } catch { return 0n }
  }, [amount, collateral.decimals])

  const router = ADDRESSES.MULTIPLY_ROUTER
  const enabled = !!user && !!router && router !== ZERO_ADDR

  const { data: walletBal, refetch: refetchBal } = useReadContract({
    address: collateral.asset, abi: ERC20_ABI, functionName: 'balanceOf', args: user ? [user] : undefined,
    query: { enabled, refetchInterval: POLL_SLOW },
  })
  const { data: allowance, refetch: refetchAllow } = useReadContract({
    address: collateral.asset, abi: ERC20_ABI, functionName: 'allowance', args: user ? [user, router] : undefined,
    query: { enabled, refetchInterval: POLL_SLOW },
  })
  const { data: delegation, refetch: refetchDeleg } = useReadContract({
    address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'borrowAllowance',
    args: user ? [user, router, debt.asset] : undefined,
    query: { enabled, refetchInterval: POLL_SLOW },
  })
  // The THIRD authorisation, and the newest. A one-step open borrows the whole exposure before the
  // collateral backing it exists, so the pool has to defer this user's health checks for the length
  // of the transaction - that is what `setSessionOperator` grants. Deliberately separate from
  // borrow delegation: "may borrow up to X for me" must not silently also mean "may defer my health
  // checks". See CHANGELOG.md items 105-107.
  const { data: isSessionOperator, refetch: refetchOperator } = useReadContract({
    address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'sessionOperator',
    args: user ? [user, router] : undefined,
    query: { enabled, refetchInterval: POLL_SLOW },
  })

  // Approval is judged against the TYPED amount, so a wallet that approved once for a small open
  // is correctly asked again for a larger one. The other two are one-time grants.
  const needsApproval = enabled && wei > 0n && (allowance ?? 0n) < wei
  const needsDelegation = enabled && (delegation ?? 0n) === 0n
  const needsSessionOperator = enabled && !isSessionOperator

  const steps = [
    { label: `Approve ${collateral.symbol}`, done: enabled && !needsApproval, help: 'Lets the router move the collateral you deposit.' },
    { label: `Authorise ${debt.symbol} borrowing`, done: enabled && !needsDelegation, help: `Lets the router borrow ${debt.symbol} on your behalf.` },
    { label: 'Authorise one-step opening', done: enabled && !needsSessionOperator, help: 'Lets the pool defer your health checks for the length of one transaction, which is what makes a single-step open possible.' },
  ]

  const hasPosition = collateral.user.supplyBalance > 0 && debt.user.debt > 0

  // ── Position, current and projected.
  const exposureUsd = collateral.user.supplyBalance * collateral.priceUsd
  const debtUsd = debt.user.debt * debt.priceUsd
  const curLev = currentLeverage(exposureUsd, debtUsd)

  const principal = Number(wei) / 10 ** collateral.decimals
  const active = wei > 0n
  // Opening at L times adds L times the principal as collateral, funded by borrowing the (L - 1)
  // difference in the debt asset.
  const addedCollateral = principal * leverage
  const addedDebtInDebtUnits = debt.priceUsd > 0
    ? (principal * (leverage - 1) * collateral.priceUsd) / debt.priceUsd
    : 0
  // The open borrows `addedDebtInDebtUnits` of the debt asset, which must fit under THAT reserve's
  // borrow cap + idle liquidity (and borrowing must not be disabled) or openOneStep reverts - the
  // same reserve-headroom check the Markets borrow form does (reserveCaps.js).
  const addedDebtWei = (() => { try { return parseUnits(addedDebtInDebtUnits.toFixed(debt.decimals), debt.decimals) } catch { return 0n } })()
  const debtHeadroomWei = effectiveBorrowMaxWei({
    collateralMaxWei: MAX_UINT,
    borrowCapWei: debt.borrowCapWei,
    totalBorrowsWei: debt.totalBorrowsWei,
    availableLiquidityWei: debt.availableLiquidityWei,
    borrowingDisabled: debt.borrowingDisabled,
  })
  const debtCapExceeded = enabled && wei > 0n && addedDebtWei > debtHeadroomWei

  const nextCollateralAmount = collateral.user.supplyBalance + addedCollateral
  const nextDebtAmount = debt.user.debt + addedDebtInDebtUnits
  const nextExposureUsd = nextCollateralAmount * collateral.priceUsd
  const nextDebtUsd = nextDebtAmount * debt.priceUsd

  // Priced in the DEBT asset, which is the ratio that actually matters for a correlated pair -
  // "wstETH falls to 0.94 ETH" is the event that liquidates this, and a dollar price obscures it.
  const curLiqPrice = liquidationPrice(collateral.user.supplyBalance, debt.user.debt, collateral.liquidationThresholdBps)
  const nextLiqPrice = liquidationPrice(nextCollateralAmount, nextDebtAmount, collateral.liquidationThresholdBps)
  const spotRatio = debt.priceUsd > 0 ? collateral.priceUsd / debt.priceUsd : 0

  // All-in on both legs: interest, HORIZON rewards, and the collateral's intrinsic yield (wstETH
  // staking). An earlier version kept HORIZON out to avoid assuming a token price, and the result
  // was a simulate panel reading 0.00% for loops whose entire return is emissions - the assumption
  // is now explicit in the row's help text instead of the number being silently smaller.
  //
  // Unlike the list page's $10k reference basis, THIS form knows the actual deposit, so the reward
  // dilution is exact: the emissions are divided over the pool as it stands after this very open.
  const legInputs = {
    supplyRateBps: collateral.supplyRateBps,
    borrowRateBps: debt.borrowRateBps,
    intrinsicAprBps: intrinsicAprBpsFor(collateral.key, lstAprBps),
    // Budget bounded by the governor caps, matching accrual - see loopLegRates' own comment.
    supplyEmissionsUsdPerYear: hasHorizonPrice ? toNumber(collateral.supplyHorizonBudgetPerSecond) * YEAR_SECONDS * horizonUsd : 0,
    borrowEmissionsUsdPerYear: hasHorizonPrice ? toNumber(debt.borrowHorizonBudgetPerSecond) * YEAR_SECONDS * horizonUsd : 0,
    supplyCapBps: collateral.supplyAprCapBps,
    borrowCapBps: debt.borrowAprCapBps,
    suppliedUsd: collateral.totalSupplied * collateral.priceUsd,
    borrowedUsd: debt.totalBorrows * debt.priceUsd,
  }
  // The existing position already sits inside the pool totals, so its rates dilute with add = 0.
  const curLegs = loopLegRates({ ...legInputs, supplyAddUsd: 0, borrowAddUsd: 0 })
  const nextLegs = loopLegRates({
    ...legInputs,
    supplyAddUsd: addedCollateral * collateral.priceUsd,
    borrowAddUsd: addedDebtInDebtUnits * debt.priceUsd,
  })
  const curNetApy = hasPosition ? netApyBps(curLegs.earnBps, curLegs.costBps, curLev) : 0
  const nextNetApy = netApyBps(nextLegs.earnBps, nextLegs.costBps, leverage)
  const exposureIfOpened = addedCollateral * collateral.priceUsd

  return (
    <Panel style={{ padding: 28 }}>
      <SectionLabel>Simulate &amp; open</SectionLabel>

      <div style={{ marginTop: 16 }}>
        <div style={fieldLabel}>
          Deposit {collateral.symbol}
          {walletBal != null && (
            <MaxLink onClick={() => setAmount(weiToInputString(walletBal, collateral.decimals))}>
              Max {fmtBalance(Number(walletBal) / 10 ** collateral.decimals)}
            </MaxLink>
          )}
        </div>
        <Input value={amount} onChange={setAmount} placeholder="0.00" />
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={fieldLabel}>
          Leverage <span className="mono" style={{ color: T.text, fontWeight: 700 }}>{leverage.toFixed(1)}x</span>
        </div>
        <input
          type="range" min={1.1} max={Math.max(1.2, loop.leverage)} step={0.1} value={leverage}
          onChange={(e) => setLeverage(Number(e.target.value))} disabled={!enabled}
          style={{ width: '100%' }}
        />
        <div style={sliderScale}>
          <span>1.1x</span>
          <span>max {loop.leverage.toFixed(1)}x</span>
        </div>
      </div>

      <AuthChecklist steps={steps} enabled={enabled} />

      <SimulationRows rows={[
        {
          label: 'Net APY',
          help: 'The spread between what your collateral earns and what the borrow costs, multiplied by leverage. Earnings count interest, HORIZON rewards at today\'s price, and native collateral yield such as wstETH staking. Reward rates are computed on the pool as it stands after your open, so what you see is what your deposit would actually capture.',
          before: curNetApy / 100,
          after: active ? nextNetApy / 100 : undefined,
          format: (v) => fmtPct(v),
          good: 'up',
        },
        {
          label: 'Leverage',
          before: curLev,
          after: active ? leverage : undefined,
          format: (v) => `${v.toFixed(2)}x`,
          good: 'neutral',
        },
        {
          label: 'Exposure',
          before: exposureUsd,
          after: active ? nextExposureUsd : undefined,
          format: (v) => fmtUsd(v),
          good: 'neutral',
        },
        {
          label: 'Debt',
          before: debtUsd,
          after: active ? nextDebtUsd : undefined,
          format: (v) => fmtUsd(v),
          good: 'down',
        },
        {
          label: 'Liquidation price',
          help: `Priced in ${debt.symbol}. Because these two assets move together, an ordinary market fall moves both legs at once. What threatens this position is the ratio between them changing.`,
          before: curLiqPrice,
          after: active ? nextLiqPrice : undefined,
          format: (v) => (v == null ? '—' : `${fmt(v, 4)} ${debt.symbol}`),
          good: 'down',
        },
        spotRatio > 0 && (active ? nextLiqPrice : curLiqPrice) != null ? {
          label: 'Room before liquidation',
          before: curLiqPrice != null ? Math.max(0, (1 - curLiqPrice / spotRatio) * 100) : null,
          after: active && nextLiqPrice != null ? Math.max(0, (1 - nextLiqPrice / spotRatio) * 100) : undefined,
          format: (v) => (v == null ? '—' : `${v.toFixed(1)}%`),
          good: 'up',
        } : null,
        {
          label: 'Estimated gas, round trip',
          help: 'Opening and closing measured against a real venue. Both legs are one swap each.',
          before: ROUND_TRIP_GAS,
          format: (v) => `${(v / 1e6).toFixed(2)}M`,
        },
      ]} />

      {/* A CONFIGURED venue is not yet a usable one. `SwapVenue` refuses to swap through a pool
          until it has aged past the router's dormancy window, so without this the button would
          revert after signing - the same failure the "venue pending" state already prevents one
          step earlier. Closing is unaffected: it swaps through the same venue, but an existing
          position predates the timer. */}
      {loop.venueDormant && !loop.openingPaused && (
        <div style={pausedNote}>
          <div style={{ color: T.warning, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Opening becomes available {new Date(loop.venueReadyAtSec * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
          </div>
          <div style={{ marginTop: 4, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
            This loop is building enough price history to support reliable swap pricing. Opening is
            delayed until then to protect against short term price manipulation.
          </div>
        </div>
      )}

      {/* Stated before the transaction, not discovered as an `OpeningIsPaused` revert after
          signing. Closing stays available throughout, which is the whole shape of this pause. */}
      {loop.openingPaused && (
        <div style={pausedNote}>
          <div style={{ color: T.warning, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            New positions are paused
          </div>
          <div style={{ marginTop: 4, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
            Closing an existing position stays available, and repaying {debt.symbol} on the Markets
            page works as usual.
          </div>
        </div>
      )}

      {/* The debt reserve can't fund this open - stated before signing rather than discovered as a
          BorrowCapExceeded/BorrowingDisabled revert. */}
      {debtCapExceeded && (
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: T.r.md, border: `1px solid ${T.warning}55`, background: `${T.warning}12`, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
          {debt.borrowingDisabled
            ? `Borrowing ${debt.symbol} is currently disabled, so this position can't be opened.`
            : `Not enough ${debt.symbol} available to borrow for this size - reduce the amount or leverage. The pool has ${fmtBalance(toNumber(debtHeadroomWei, debt.decimals))} ${debt.symbol} of borrow room right now.`}
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button
          onClick={submitOpen}
          disabled={!enabled || action.pending || loop.openingPaused || loop.venueDormant || wei === 0n || debtCapExceeded}
          style={{ flex: 1, minWidth: 160 }}
        >
          {loop.openingPaused ? 'Opening paused'
            : loop.venueDormant ? 'Venue warming up'
            : action.pending ? 'Working…'
            : 'Open position'}
        </Button>
        {hasPosition && (
          <Button onClick={submitClose} disabled={action.pending} variant="secondary">
            Close
          </Button>
        )}
      </div>

      {/* Stated up front rather than discovered after paying. A measured round trip is ~1.62M gas
          (810k open, 806k close), about $31 at 10 gwei - so the cost is roughly 0.6% of a $5,000
          position and proportionally less above that. The threshold moved down from $25,000 when
          the quoted figure was corrected: the old number described the iterative open this form no
          longer calls. */}
      {active && exposureIfOpened > 0 && exposureIfOpened < 5_000 && (
        <div style={{ color: T.warning, marginTop: 12, fontSize: T.fs.caption, lineHeight: 1.5 }}>
          Opening and closing costs about 1.62M gas in total, roughly $31 at 10 gwei. On a position
          this size that is a meaningful share of what you put in, so Multiply repays the fixed cost
          best above about $5,000.
        </div>
      )}
      {action.error && <div style={{ color: T.negative, marginTop: 10, fontSize: T.fs.caption }}>{String(action.error)}</div>}
      {!user && <div style={{ color: T.textMuted, marginTop: 10, fontSize: T.fs.caption }}>Connect a wallet to open a position.</div>}
    </Panel>
  )

  function refetchAll() {
    refetchBal(); refetchAllow(); refetchDeleg(); refetchOperator()
    onTx?.()
  }

  async function submitOpen() {
    // The three authorisations that used to be three separate button presses before the open are
    // now one intent with the open itself: the collateral approval as a pull, the borrow delegation
    // and the one-step session-operator grant as leading pool calls (only when short), then
    // openOneStep. On a batch wallet that is a SINGLE confirmation; on a legacy wallet the prompts
    // still fire in order but without the manual re-click between each. See INTENT-UX-PLAN.md and,
    // for why the three grants are distinct authorisations, CHANGELOG.md items 105-107.
    const calls = []
    if (needsDelegation) calls.push({ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'approveBorrowDelegation', args: [router, debt.asset, MAX_UINT] })
    if (needsSessionOperator) calls.push({ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'setSessionOperator', args: [router, true] })
    // minCollateralAdded floors the WHOLE operation rather than any single pass. Set to the
    // principal itself: the open must at least not lose money outright on swap costs before it
    // has bought any extra exposure. A tighter bound would revert on ordinary slippage; a looser
    // one would not bound anything a user cares about.
    //
    // `openOneStep`, not `open`: the iterative version pays a swap fee and slippage on EVERY pass,
    // and the last passes buy very little exposure for the same cost. One swap is strictly better
    // wherever the reserve can absorb it. `open` is kept on the contract as the fallback for pairs
    // with no configured swap venue or a reserve too thin for the whole swap at once - if that case
    // ever needs reaching from the UI it wants its own explicit control, not a silent fallback that
    // makes the cost of an open unpredictable.
    calls.push({ address: router, abi: multiplyAbi, functionName: 'openOneStep', args: [collateral.asset, debt.asset, wei, BigInt(Math.round(leverage * 10_000)), wei] })
    // Permit2 path (non-batch wallets): the collateral approve becomes a signature via
    // openOneStepWithPermit; the delegation + session-operator grants (calls before the open) stay
    // on-chain preCalls since they are pool-state authorisations, not token pulls.
    const res = await action.run({
      pulls: [{ token: collateral.asset, spender: router, amount: wei }],
      calls,
      permit: {
        token: collateral.asset, amount: wei, spender: router,
        preCalls: calls.slice(0, -1), // everything except the final openOneStep (delegation, session op)
        buildCall: (p, sig) => ({ address: router, abi: multiplyAbi, functionName: 'openOneStepWithPermit', args: [debt.asset, BigInt(Math.round(leverage * 10_000)), wei, p, sig] }),
      },
      successMsg: `Opened ${leverage}x ${collateral.symbol}.`,
    })
    if (!res) return
    setAmount('')
    refetchAll()
  }

  async function submitClose() {
    // The router pulls the collateral sToken to unwind, so close carries that approval as a pull
    // (MAX sentinel: re-approves only when the allowance is not already infinite).
    const res = await action.run({
      pulls: [{ token: collateral.sTokenAddress, spender: router, amount: MAX_UINT }],
      calls: [{ address: router, abi: multiplyAbi, functionName: 'close', args: [collateral.asset, debt.asset, 20n, 0n] }],
      successMsg: 'Position unwound.',
    })
    if (res) refetchAll()
  }
}

/// All three authorisations, visible from the start. See LoopForm's own doc comment for why.
function AuthChecklist({ steps, enabled }) {
  const remaining = steps.filter((s) => !s.done).length
  if (!enabled || remaining === 0) return null

  return (
    <div style={{
      marginTop: 16, padding: '14px 16px', borderRadius: T.r.md,
      border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.02)',
    }}>
      <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {remaining === 3 ? 'Three approvals before the first open' : `${remaining} approval${remaining > 1 ? 's' : ''} remaining`}
      </div>
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 9 }}>
        {steps.map((s, i) => (
          <div key={s.label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{
              width: 18, height: 18, flexShrink: 0, borderRadius: '50%', marginTop: 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${s.done ? T.positive : T.border}`,
              background: s.done ? T.positiveDim : 'transparent',
              color: s.done ? T.positive : T.textDim,
              fontFamily: T.fontN, fontSize: 10, fontWeight: 700,
            }}>
              {s.done ? '✓' : i + 1}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: s.done ? T.textDim : T.text, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700 }}>
                {s.label}
              </div>
              <div style={{ marginTop: 2, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
                {s.help}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const pausedNote = {
  marginTop: 16, padding: '12px 14px', borderRadius: T.r.md,
  border: `1px solid ${T.warning}44`, background: `${T.warning}11`,
}
const fieldLabel = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, marginBottom: 6,
}
const sliderScale = {
  display: 'flex', justifyContent: 'space-between', marginTop: 4,
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption,
}
